import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnSizingState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import React, { useState } from "react";
import {
  MONTH_NAMES,
  type DataItem,
  type HeaderData,
  type Hemisphere,
  type MonthName,
  type ScheduleFilter,
} from "../types";

export interface ResizableTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enablePagination?: boolean;
  enableColumnResizing?: boolean;
  enableRowSelection?: boolean;
  pageSize?: number;
  className?: string;
  onRowClick?: (row: T) => void;
  getRowProps?: (row: T) => React.HTMLAttributes<HTMLTableRowElement>;
  emptyMessage?: React.ReactNode;
  // ACNH-specific props
  isACNHTable?: boolean;
  headerData?: HeaderData;
  hemisphere?: Hemisphere;
  currentMonth?: MonthName | "AllMonths";
  scheduleFilter?: ScheduleFilter;
}

function ResizableTable<T>({
  data,
  columns: externalColumns,
  enableSorting = true,
  enableFiltering = false,
  enablePagination = false,
  enableColumnResizing = true,
  enableRowSelection = false,
  pageSize = 10,
  className = "",
  onRowClick,
  getRowProps: externalGetRowProps,
  emptyMessage = "No data available",
  // ACNH-specific props
  isACNHTable = false,
  headerData,
  hemisphere,
  currentMonth,
  scheduleFilter,
}: ResizableTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  // ACNH-specific logic
  const getRowStatus = (
    item: DataItem,
  ): { isNew: boolean; isEnding: boolean } => {
    if (!currentMonth || currentMonth === "AllMonths") {
      return { isNew: false, isEnding: false };
    }

    const monthIndex = MONTH_NAMES.indexOf(currentMonth as MonthName) + 1;

    const isNew =
      hemisphere === "northern"
        ? item.northStartMonth1 === monthIndex ||
          item.northStartMonth2 === monthIndex
        : item.southStartMonth1 === monthIndex ||
          item.southStartMonth2 === monthIndex;

    const isEnding =
      hemisphere === "northern"
        ? item.northEndMonth1 === monthIndex ||
          item.northEndMonth2 === monthIndex
        : item.southEndMonth1 === monthIndex ||
          item.southEndMonth2 === monthIndex;

    return { isNew, isEnding };
  };

  const shouldShowRow = (item: DataItem): boolean => {
    const { isNew, isEnding } = getRowStatus(item);

    if (scheduleFilter === "new" && !isNew) return false;
    if (scheduleFilter === "end" && !isEnding) return false;

    return true;
  };

  // ACNH column definitions
  const acnhColumns: ColumnDef<DataItem>[] = [
    {
      id: "image",
      header: headerData?.image || "Image",
      accessorKey: "image",
      size: 80,
      enableResizing: true,
      enableSorting: false,
      cell: ({ getValue, row }) => (
        <img
          src={getValue() as string}
          alt={row.original.name}
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "var(--radius-sm)",
            objectFit: "cover",
          }}
        />
      ),
    },
    {
      id: "name",
      header: headerData?.name || "Name",
      accessorKey: "name",
      size: 200,
      enableResizing: true,
      enableSorting: true,
      cell: ({ getValue }) => (
        <span style={{ fontWeight: "var(--font-weight-medium)" }}>
          {getValue() as string}
        </span>
      ),
    },
    {
      id: "date",
      header:
        hemisphere === "northern"
          ? headerData?.northernDate
          : headerData?.southernDate,
      size: 150,
      enableResizing: true,
      enableSorting: false,
      cell: ({ row }) => (
        <span style={{ color: "var(--color-text-secondary)" }}>
          {hemisphere === "northern"
            ? row.original.northernDate
            : row.original.southernDate}
        </span>
      ),
    },
    {
      id: "time",
      header: headerData?.time || "Time",
      accessorKey: "time",
      size: 120,
      enableResizing: true,
      enableSorting: false,
      cell: ({ getValue }) => (
        <span style={{ color: "var(--color-text-secondary)" }}>
          {getValue() as string}
        </span>
      ),
    },
    {
      id: "price",
      header: headerData?.price || "Price",
      accessorKey: "price",
      size: 120,
      enableResizing: true,
      enableSorting: true,
      cell: ({ getValue }) => (
        <span
          style={{
            fontWeight: "var(--font-weight-semibold)",
            color: "var(--color-success)",
          }}
        >
          {(getValue() as number).toLocaleString()} Bells
        </span>
      ),
    },
    {
      id: "type",
      header: headerData?.type || "Type",
      accessorKey: "type",
      size: 100,
      enableResizing: true,
      enableSorting: true,
      cell: ({ getValue }) => (
        <span
          style={{
            padding: "var(--space-xs) var(--space-sm)",
            backgroundColor: "var(--color-primary-light)",
            color: "var(--color-primary)",
            borderRadius: "var(--radius-sm)",
            fontSize: "var(--font-size-xs)",
            fontWeight: "var(--font-weight-medium)",
          }}
        >
          {getValue() as string}
        </span>
      ),
    },
    {
      id: "size",
      header: headerData?.size || "Size",
      accessorKey: "size",
      size: 100,
      enableResizing: true,
      enableSorting: false,
      cell: ({ getValue }) => (
        <span style={{ color: "var(--color-text-secondary)" }}>
          {getValue() as string}
        </span>
      ),
    },
    {
      id: "location",
      header: headerData?.location || "Location",
      accessorKey: "location",
      size: 150,
      enableResizing: true,
      enableSorting: true,
      cell: ({ getValue }) => (
        <span style={{ color: "var(--color-text-secondary)" }}>
          {getValue() as string}
        </span>
      ),
    },
  ];

  // ACNH row props for status styling
  const getACNHRowProps = (record: DataItem) => {
    const { isNew, isEnding } = getRowStatus(record);

    let className = "";
    let style: React.CSSProperties = {};

    if (isNew && isEnding) {
      className = "new-ending-item";
      style = {
        borderLeft: "4px solid var(--color-primary)",
        background:
          "linear-gradient(90deg, var(--color-success-light) 50%, var(--color-warning-light) 50%)",
      };
    } else if (isNew) {
      className = "new-item";
      style = {
        borderLeft: "4px solid var(--color-success)",
        backgroundColor: "var(--color-success-light)",
      };
    } else if (isEnding) {
      className = "ending-item";
      style = {
        borderLeft: "4px solid var(--color-warning)",
        backgroundColor: "var(--color-warning-light)",
      };
    }

    return { className, style };
  };

  // Determine which columns and data to use
  const columns = isACNHTable
    ? (acnhColumns as ColumnDef<T>[])
    : externalColumns;
  const tableData = isACNHTable
    ? ((data as DataItem[]).filter(shouldShowRow) as T[])
    : data;

  const getRowProps = isACNHTable
    ? (row: T) => getACNHRowProps(row as DataItem)
    : externalGetRowProps;

  const table = useReactTable({
    data: tableData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: enablePagination
      ? getPaginationRowModel()
      : undefined,
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      columnSizing,
    },
    initialState: {
      pagination: {
        pageSize,
      },
    },
    enableRowSelection,
    enableColumnResizing,
  });

  // Early return for invalid ACNH table
  if (isACNHTable && !headerData?.name) {
    return null;
  }

  return (
    <>
      <style>{`
        .resizable-table {
          width: 100%;
          table-layout: fixed;
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          overflow: hidden;
          box-shadow: var(--shadow-sm);
          border-collapse: separate;
          border-spacing: 0;
          border: 1px solid var(--color-border);
        }

        .resizable-table thead {
          background: ${
            isACNHTable && hemisphere === "northern"
              ? "linear-gradient(135deg, var(--color-northern) 0%, #5856eb 100%)"
              : isACNHTable && hemisphere === "southern"
                ? "linear-gradient(135deg, var(--color-southern) 0%, #0891b2 100%)"
                : "linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%)"
          };
          color: var(--color-text-inverse);
        }

        .resizable-table th {
          padding: var(--space-md) var(--space-lg);
          font-weight: var(--font-weight-semibold);
          font-size: var(--font-size-sm);
          text-align: left;
          border-right: 1px solid rgba(255, 255, 255, 0.1);
          position: relative;
          user-select: none;
        }

        .resizable-table th:last-child {
          border-right: none;
        }

        .resizable-table th.sortable {
          cursor: pointer;
        }

        .resizable-table th.sortable:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .resizable-table .resizer {
          position: absolute;
          right: 0;
          top: 0;
          height: 100%;
          width: 5px;
          background: rgba(255, 255, 255, 0.1);
          cursor: col-resize;
          user-select: none;
          touch-action: none;
          opacity: 0;
          transition: opacity var(--transition-fast);
        }

        .resizable-table th:hover .resizer {
          opacity: 1;
        }

        .resizable-table .resizer.isResizing {
          background: var(--color-accent);
          opacity: 1;
        }

        .resizable-table tbody {
          background: var(--color-surface);
        }

        .resizable-table tr {
          transition: all var(--transition-fast);
          border-bottom: 1px solid var(--color-border);
        }

        .resizable-table tr:last-child {
          border-bottom: none;
        }

        .resizable-table tbody tr:hover {
          background: var(--color-surface-hover);
          transform: translateY(-1px);
          box-shadow: var(--shadow-sm);
        }

        .resizable-table td {
          padding: var(--space-md) var(--space-lg);
          font-size: var(--font-size-sm);
          color: var(--color-text-primary);
          border-right: 1px solid var(--color-border);
          vertical-align: middle;
          word-wrap: break-word;
          overflow: hidden;
        }

        .resizable-table td:last-child {
          border-right: none;
        }

        .sort-indicator {
          margin-left: var(--space-xs);
          font-size: var(--font-size-xs);
          opacity: 0.7;
        }

        .table-empty {
          text-align: center;
          padding: var(--space-xl);
          color: var(--color-text-secondary);
        }

        .table-empty-icon {
          font-size: 2rem;
          margin-bottom: var(--space-sm);
          opacity: 0.5;
        }

        .pagination-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-md) var(--space-lg);
          background: var(--color-surface);
          border-top: 1px solid var(--color-border);
          border-radius: 0 0 var(--radius-lg) var(--radius-lg);
        }

        .pagination-info {
          color: var(--color-text-secondary);
          font-size: var(--font-size-sm);
        }

        .pagination-buttons {
          display: flex;
          gap: var(--space-xs);
        }

        .pagination-button {
          padding: var(--space-xs) var(--space-sm);
          font-size: var(--font-size-xs);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          background: var(--color-surface);
          color: var(--color-text-secondary);
          cursor: pointer;
          transition: all var(--transition-fast);
          min-width: 32px;
          text-align: center;
        }

        .pagination-button:hover:not(:disabled) {
          background: var(--color-surface-hover);
          border-color: var(--color-border-strong);
          color: var(--color-text-primary);
        }

        .pagination-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-button.active {
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: var(--color-text-inverse);
        }

        /* ACNH-specific styles */
        .new-item:hover {
          background-color: var(--color-success-light) !important;
        }
        
        .ending-item:hover {
          background-color: var(--color-warning-light) !important;
        }
        
        .new-ending-item:hover {
          background: linear-gradient(90deg, var(--color-success-light) 50%, var(--color-warning-light) 50%) !important;
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .resizable-table {
            display: none;
          }
          
          .table-mobile {
            display: block;
          }
        }

        @media (min-width: 769px) {
          .table-mobile {
            display: none;
          }
        }

        .mobile-card {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-md);
          margin-bottom: var(--space-md);
          box-shadow: var(--shadow-sm);
        }

        .mobile-card:hover {
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }

        .mobile-card-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-xs) 0;
          border-bottom: 1px solid var(--color-border);
        }

        .mobile-card-row:last-child {
          border-bottom: none;
        }

        .mobile-card-label {
          font-weight: var(--font-weight-medium);
          color: var(--color-text-secondary);
          font-size: var(--font-size-xs);
        }

        .mobile-card-value {
          color: var(--color-text-primary);
          font-size: var(--font-size-sm);
        }

        .data-count {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-md) var(--space-lg);
          fontSize: var(--font-size-sm);
          font-weight: var(--font-weight-medium);
          color: var(--color-text-secondary);
          text-align: center;
          margin-bottom: var(--space-lg);
          box-shadow: var(--shadow-sm);
        }
      `}</style>

      <div>
        {/* ACNH Data Count */}
        {isACNHTable && (
          <div className="data-count">
            Currently Displaying: {tableData.length} items
          </div>
        )}

        {/* Desktop Table */}
        <table className={`resizable-table ${className}`}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={header.column.getCanSort() ? "sortable" : ""}
                    style={{
                      width: header.getSize(),
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                    {header.column.getCanSort() && (
                      <span className="sort-indicator">
                        {{
                          asc: "↑",
                          desc: "↓",
                        }[header.column.getIsSorted() as string] ?? "↕"}
                      </span>
                    )}
                    {enableColumnResizing && (
                      <div
                        {...{
                          onMouseDown: header.getResizeHandler(),
                          onTouchStart: header.getResizeHandler(),
                          className: `resizer ${
                            header.column.getIsResizing() ? "isResizing" : ""
                          }`,
                        }}
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={table.getAllColumns().length}
                  className="table-empty"
                >
                  {isACNHTable ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "var(--space-xl)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "2rem",
                          marginBottom: "var(--space-sm)",
                          opacity: 0.5,
                        }}
                      >
                        🎣
                      </div>
                      <div style={{ color: "var(--color-text-secondary)" }}>
                        No collectibles found with current filters
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="table-empty-icon">📂</div>
                      <div>{emptyMessage}</div>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const rowProps = getRowProps?.(row.original) || {};
                return (
                  <tr
                    key={row.id}
                    {...rowProps}
                    onClick={() => onRowClick?.(row.original)}
                    style={{
                      cursor: onRowClick ? "pointer" : "default",
                      ...rowProps.style,
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Mobile Cards */}
        <div className="table-mobile">
          {table.getRowModel().rows.length === 0 ? (
            <div className="table-empty">
              {isACNHTable ? (
                <div
                  style={{ textAlign: "center", padding: "var(--space-xl)" }}
                >
                  <div
                    style={{
                      fontSize: "2rem",
                      marginBottom: "var(--space-sm)",
                      opacity: 0.5,
                    }}
                  >
                    🎣
                  </div>
                  <div style={{ color: "var(--color-text-secondary)" }}>
                    No collectibles found with current filters
                  </div>
                </div>
              ) : (
                <>
                  <div className="table-empty-icon">📂</div>
                  <div>{emptyMessage}</div>
                </>
              )}
            </div>
          ) : (
            table.getRowModel().rows.map((row) => {
              const rowProps = getRowProps?.(row.original) || {};
              return (
                <div
                  key={row.id}
                  className={`mobile-card ${rowProps.className || ""}`}
                  style={rowProps.style}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <div key={cell.id} className="mobile-card-row">
                      <span className="mobile-card-label">
                        {typeof cell.column.columnDef.header === "string"
                          ? cell.column.columnDef.header
                          : cell.column.id}
                      </span>
                      <span className="mobile-card-value">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {enablePagination && tableData.length > pageSize && (
          <div className="pagination-controls">
            <div className="pagination-info">
              Showing {table.getState().pagination.pageIndex * pageSize + 1}-
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * pageSize,
                tableData.length,
              )}{" "}
              of {tableData.length} items
            </div>
            <div className="pagination-buttons">
              <button
                className="pagination-button"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                {"<<"}
              </button>
              <button
                className="pagination-button"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {"<"}
              </button>
              <button
                className="pagination-button"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {">"}
              </button>
              <button
                className="pagination-button"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                {">>"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ResizableTable;
